<template>
  <div
    class="custom-ui-dropdown-wrapper"
    v-click-outside="close"
    :class="expanded ? 'expanded' : ''"
  >
    <div class="custom-ui-dropdown-button" :class="classes" @click="openDropdown">
      <span class="custom-ui-dropdown-icon" v-if="icon">
        <i :class="icon" />
      </span>
      <span class="custom-ui-dropdown-label" v-text="label" />
      <span class="custom-ui-dropdown-arrow" v-if="!disabled">
        <i class="fa-solid" :class="isActive ? 'fa-caret-up' : 'fa-caret-down'" />
      </span>
    </div>
    <div class="custom-ui-dropdown" v-if="isActive && !isSlot">
      <div class="dropdown-search" v-if="searchable">
        <ui-input v-model="searchKeyword" size="small" :placeholder="t('admin_global_search')" />
      </div>
      <div
        class="custom-ui-dropdown-item"
        v-for="(dItem, dIndex) in searchedList"
        :key="dIndex"
        @click="select(dItem)"
      >
        {{ dItem[name] }}
      </div>
    </div>
    <div class="custom-ui-dropdown" v-if="isActive && isSlot" @click="close">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { IUIDropdown } from '@/types/Components'
  import { UseI18n } from '@/utils-ts'
  import { computed, ref } from 'vue'

  const {
    label = '',
    icon = '',
    type = 'primary',
    size = 'sm',
    disabled = false,
    items = [],
    name = '',
    value,
    isSlot = false,
    searchable = false,
    expanded = false,
  } = defineProps<IUIDropdown>()

  const emit = defineEmits<{
    (e: 'select', item: any): void
  }>()
  const { t } = UseI18n()

  const isActive = ref<boolean>(false)
  const searchKeyword = ref<string>('')

  const classes = computed(() => {
    return [`type-${type}`, `size-${size}`, disabled ? 'disabled' : '', expanded ? 'expanded' : '']
  })

  const searchedList = computed(() => {
    if (searchable) {
      return items.filter(item => {
        return item[name].toLowerCase().includes(searchKeyword.value.toLowerCase())
      })
    } else {
      return items
    }
  })

  const close = () => {
    isActive.value = false
  }

  const openDropdown = () => {
    if (!disabled) {
      isActive.value = !isActive.value
    }
  }

  const select = (item: any) => {
    if (value) {
      emit('select', item[value])
    } else {
      emit('select', item)
    }
    close()
  }
</script>

<style lang="scss" scoped>
  .custom-ui-dropdown-wrapper {
    position: relative;
    @include flexible;
    border-radius: 5px;
    &.expanded {
      width: 100%;
    }
    .custom-ui-dropdown-button {
      padding: calc(0.5em) 1em;
      @include flexible($direction: row, $justify: space-between, $align: center);
      border-radius: 5px;
      cursor: pointer;
      &.expanded {
        width: 100%;
      }

      &.disabled {
        cursor: not-allowed;
        background-color: $disabled;
      }
      .custom-ui-dropdown-icon {
        margin-right: 0.75em;
      }
      .custom-ui-dropdown-label {
        white-space: nowrap;
      }
      .custom-ui-dropdown-arrow {
        margin-left: 0.75rem;
        @include flexible($justify: flex-end);
      }
    }
    .custom-ui-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      width: auto;
      min-width: 160px;
      background-color: $white;
      border-radius: 5px;
      border: 1px solid $border-color;
      z-index: 99;
      height: auto;

      max-height: 300px;
      box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.2);
      overflow-y: auto;
      @include flexible($direction: column);
      &::-webkit-scrollbar {
        width: 6px;
        background-color: #fff;
      }
      &::-webkit-scrollbar-thumb:vertical {
        background-color: $textColor;
        border-radius: 10px;
      }
      .dropdown-search {
        padding: 0.5rem;
        width: 100%;
      }
      .custom-ui-dropdown-item {
        padding: 0.5rem;
        cursor: pointer;
        width: 100%;
        display: flex;
        font-weight: 500;
        font-size: 0.825rem;
        &:hover {
          background-color: $border-color;
        }
      }
    }
  }
</style>
